// src/pages/Dashboard/Admin/Votes/VotesDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { useVotesDashboard } from "./hooks/useVotesDashboard";

import DashboardVotesHeader from "./components/DashboardVotesHeader";
import VotesKpiCards from "./components/VotesKpiCards";
import VotesFiltersCard from "./components/VotesFiltersCard";
import VotesTable from "./components/VotesTable";
import VoteFormDrawer from "./components/VoteFormDrawer";
import VoteQuestionsDrawer from "./components/VoteQuestionsDrawer";
import VoteResultsDrawer from "./components/VoteResultsDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedVotesPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Votes en ligne
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function VotesDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const dashboard = useVotesDashboard();

  if (roleId !== 1) {
    return <UnauthorizedVotesPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardVotesHeader
          loading={dashboard.loading}
          onCreate={dashboard.openCreateDrawer}
          onRefresh={dashboard.fetchVotes}
        />

        <VotesKpiCards
          stats={dashboard.stats}
          filteredStats={dashboard.filteredStats}
          loading={dashboard.loading}
        />

        <VotesFiltersCard
          filters={dashboard.filters}
          setFilter={dashboard.setFilter}
          resetFilters={dashboard.resetFilters}
          filteredCount={dashboard.filteredVotes.length}
          totalCount={dashboard.votes.length}
          loading={dashboard.loading}
          onRefresh={dashboard.fetchVotes}
        />

        <VotesTable
          votes={dashboard.filteredVotes}
          loading={dashboard.loading}
          deletingId={dashboard.deletingId}
          onEdit={dashboard.openEditDrawer}
          onDelete={dashboard.handleDeleteVote}
          onQuestions={dashboard.openQuestionsDrawer}
          onResults={dashboard.openResultsDrawer}
        />
      </div>

      <VoteFormDrawer
        isOpen={dashboard.formDrawerOpen}
        mode={dashboard.formMode}
        vote={dashboard.editableVote}
        submitting={dashboard.submittingForm}
        onClose={dashboard.closeFormDrawer}
        onSubmit={
          dashboard.formMode === "create"
            ? dashboard.handleCreateVote
            : dashboard.handleUpdateVote
        }
      />

      <VoteQuestionsDrawer
        isOpen={dashboard.questionsDrawerOpen}
        vote={dashboard.selectedVote}
        onClose={dashboard.closeQuestionsDrawer}
        onUpdated={dashboard.fetchVotes}
      />

      <VoteResultsDrawer
        isOpen={dashboard.resultsDrawerOpen}
        vote={dashboard.selectedVote}
        onClose={dashboard.closeResultsDrawer}
      />

      <ToastContainer />
    </section>
  );
}
